import { getCategoryLabel } from './categories'
import { buildDefaultSurveyCustomFields } from './defaultSurveyFields'
import { createCategoryLandingDefaultFormFields } from './formFields'
import { getCategoryLandingHeroImage } from './heroImages'
import { leadGenFullbleedTemplateStyleForCategory } from './leadGenFullbleedKeys'

const GENERIC_COPY = {
  HIDDEN_INSURANCE_MONEY: {
    heroTitle: '미청구·미수령 보험금 가능성을 확인해보세요',
    heroSubtitle: '가입 내역과 과거 사고·진료 이력을 기준으로 확인 범위와 다음 절차를 안내합니다.',
    formTitle: '확인 상담 신청',
  },
  COVERAGE_ANALYSIS: {
    heroTitle: '내 보험의 중복·누락 보장을 기준표로 점검합니다',
    heroSubtitle: '현재 가입 내역을 기준으로 보장 공백, 중복 담보, 보험료 지속 가능성을 정리합니다.',
    formTitle: '보장분석 상담 신청',
  },
  INSURANCE_REMODELING: {
    heroTitle: '유지할 보장과 조정할 보장을 기준별로 구분합니다',
    heroSubtitle: '현재 가입 구조를 바탕으로 보장 공백, 중복 담보, 보험료 지속 가능성을 함께 점검합니다.',
    formTitle: '보험 점검 상담 신청',
  },
}

const GENERIC_COPY_VARIANTS = {
  HIDDEN_INSURANCE_MONEY: {
    'clean-form': {
      heroTitle: '미청구·미수령 보험금 가능성을 확인해보세요',
      heroSubtitle: '기억나는 가입 정보와 기본 연락처를 바탕으로 확인 범위와 다음 절차를 안내합니다.',
      formTitle: '확인 상담 신청',
    },
    'hanwha-sections': {
      heroTitle: '잊고 있던 보험금, 지금 확인해 보세요',
      heroSubtitle: '기억나는 정보만 남겨도 미청구·미수령 가능성을 함께 살펴보고, \n 다음 절차를 안내해드립니다.',
      formTitle: '숨은보험금 확인 신청',
    },
    'basic-template-4': {
      heroTitle: '보험금 확인 범위와 준비 자료를 차분히 안내합니다',
      heroSubtitle: '미청구·미수령 가능성을 상담 기준에 맞춰 확인하고 필요한 경우 청구 준비 방향을 정리합니다.',
      formTitle: '확인 요청 남기기',
    },
    'basic-template-5': {
      heroTitle: '잠들어 있던 보험금, 조용히 지나가게 두지 마세요',
      heroSubtitle: '숨은 보험금 가능 여부부터 확인하고 필요한 경우 청구 방향까지 차분하게 도와드립니다.',
      formTitle: '전문 상담 요청',
    },
  },
  COVERAGE_ANALYSIS: {
    'clean-form': {
      heroTitle: '내 보험의 중복·누락 보장을 기준표로 점검합니다',
      heroSubtitle: '현재 가입 보험을 기준으로 보장 공백, 중복 담보, 보험료 지속 가능성을 정리합니다.',
      formTitle: '보장 점검 상담 신청',
    },
    'hanwha-sections': {
      heroTitle: '가입 중인 보험을 기준으로 보장 구조를 정리합니다',
      heroSubtitle: '보험증권과 현재 상황을 바탕으로 부족한 보장, 겹치는 담보, 유지가 필요한 항목을 구분합니다.',
      formTitle: '보장 점검 신청',
    },
    'basic-template-4': {
      heroTitle: '가입보다 먼저, 현재 보장 구조부터 진단해보세요',
      heroSubtitle: '현재 보장에서 부족한 부분과 중복된 부분을 기준에 맞춰 짚어드립니다.',
      formTitle: '구조 진단 요청',
    },
    'basic-template-5': {
      heroTitle: '복잡한 보험, 핵심만 선별해 전략적으로 정리하세요',
      heroSubtitle: '현재 가입 구조를 차분히 점검하고 꼭 손봐야 할 부분부터 우선순위대로 정리해드립니다.',
      formTitle: '1:1 분석 상담',
    },
  },
  INSURANCE_REMODELING: {
    'clean-form': {
      heroTitle: '유지할 보장과 조정할 보장을 기준별로 구분합니다',
      heroSubtitle: '보험료 부담과 보장 구성을 현재 생활 기준으로 점검합니다.',
      formTitle: '보험 점검 상담 신청',
    },
    'hanwha-sections': {
      heroTitle: '유지할 것과 줄일 것을 한 번에 점검하는 보험 리모델링',
      heroSubtitle: '가족 구성과 월 보험료 부담을 기준으로, 유지·조정·보완 포인트를 한 번에 짚어드립니다.',
      formTitle: '리모델링 진단 신청',
    },
    'basic-template-4': {
      heroTitle: '보험료 부담과 보장 구조를 함께 점검합니다',
      heroSubtitle: '불필요한 조정보다 유지할 보장과 보완할 보장을 먼저 구분합니다.',
      formTitle: '점검 상담 요청',
    },
    'basic-template-5': {
      heroTitle: '현재 삶에 맞춘 보장 재설계, 지금 시작하세요',
      heroSubtitle: '지금 생활에 맞는 보장으로 다시 정리하고 보험료 부담까지 함께 점검해드립니다.',
      formTitle: '재설계 상담 요청',
    },
  },
}

function getGenericCopy(category, presetKey = 'default') {
  const variantKey = presetKey === 'lead-gen-fullbleed' ? 'hanwha-sections' : presetKey
  const fallback = {
    heroTitle: `${getCategoryLabel(category)} 랜딩페이지`,
    heroSubtitle: '카테고리 성격에 맞는 핵심 안내와 상담 연결을 제공합니다.',
    formTitle: '상담 신청',
  }
  return {
    ...(GENERIC_COPY[category] || fallback),
    ...(GENERIC_COPY_VARIANTS[category]?.[variantKey] || {}),
  }
}

function buildGenericSections(category) {
  const label = getCategoryLabel(category)
  return [
    {
      type: 'icon-grid',
      title: '',
      columns: 3,
      items: [
        { icon: '📌', title: '주요 기준 확인', description: '상담 전 먼저 볼 내용을 정리했습니다.' },
        { icon: '🧾', title: '상담 정보 접수', description: '필요한 정보를 남기면 상담으로 이어집니다.' },
        { icon: '📲', title: '바로 문의', description: '궁금하신 순간 언제든 신청하실 수 있습니다.' },
      ],
    },
    {
      type: 'text',
      title: `${label}가 필요한 경우`,
      content: `생활 단계나 가족 상황이 바뀌었거나, 지금 가입이 충분한지 확인이 필요할 때 ${label} 상담을 통해 방향을 잡을 수 있습니다.`,
    },
    {
      type: 'benefit-cards',
      title: '이렇게 도와드립니다',
      items: [
        { icon: '✅', title: '기준 중심 설명', description: '어려운 용어보다 판단 기준을 중심으로 안내합니다.' },
        { icon: '✅', title: '내 상황 기준', description: '지금의 소득·가족 구성에 맞춰 짚어드립니다.' },
        { icon: '✅', title: '부담 없는 상담', description: '신청만으로도 궁금한 점을 먼저 나눌 수 있습니다.' },
      ],
    },
  ]
}

function getTemplate1ContentSections(category) {
  if (category === 'HIDDEN_INSURANCE_MONEY') {
    return [
      {
        type: 'text',
        title: '왜 지금 확인해야 할까요?',
        content: '청구 시기를 놓치거나 가입 사실을 정확히 기억하지 못해 그대로 지나가는 보험금이 있을 수 있습니다.\n간단한 확인만으로도 놓친 권리를 찾을 가능성이 있습니다.',
      },
      {
        type: 'faq',
        title: '조회 전 자주 묻는 질문',
        items: [
          { question: '복잡한 서류가 꼭 필요한가요?', answer: '처음에는 기본 정보만으로도 상담을 시작할 수 있습니다.' },
          { question: '기억이 잘 나지 않아도 진행할 수 있나요?', answer: '네. 기억나는 범위만 남겨주셔도 확인 방향을 잡을 수 있습니다.' },
        ],
      },
      {
        type: 'process-steps',
        title: '진행 방식',
        steps: [
          { number: 1, icon: '📝', title: '조회 신청', description: '기본 정보를 남겨주세요.' },
          { number: 2, icon: '🔎', title: '전문 확인', description: '누락 가능성을 함께 살펴봅니다.' },
          { number: 3, icon: '📞', title: '후속 상담', description: '필요한 경우 다음 절차까지 안내합니다.' },
        ],
      },
    ]
  }

  if (category === 'COVERAGE_ANALYSIS') {
    return [
      {
        type: 'stats',
        title: '보장분석에서 함께 보는 항목',
        items: [
          { label: '중복 보장', value: '우선 점검' },
          { label: '누락 보장', value: '핵심 확인' },
          { label: '보험료 효율', value: '함께 검토' },
          { label: '상담 방식', value: '1:1' },
        ],
      },
      {
        type: 'text',
        title: '이런 고민이 있다면 분석이 필요합니다',
        content: '무슨 보험에 가입했는지 잘 모르겠거나, 보험료는 많이 내는데 충분한 보장을 받고 있는지 애매하다면 지금 구조를 점검해볼 필요가 있습니다.',
      },
      {
        type: 'process-steps',
        title: '간편한 분석 절차',
        steps: [
          { number: 1, icon: '🧾', title: '현재 가입 내용 확인', description: '가입 상태를 먼저 파악합니다.' },
          { number: 2, icon: '📊', title: '구조 분석', description: '중복과 누락을 중심으로 점검합니다.' },
          { number: 3, icon: '💬', title: '맞춤 제안', description: '현재 상황에 맞는 방향을 설명드립니다.' },
        ],
      },
    ]
  }

  if (category === 'INSURANCE_REMODELING') {
    return [
      {
        type: 'comparison',
        title: '보험 리모델링이 필요한 이유',
        before: {
          label: '점검 전',
          title: '현재 생활과 맞지 않는 구조',
          description: '예전 기준으로 가입한 보험이 지금의 가족 구성, 직업, 예산과 맞지 않을 수 있습니다.',
        },
        after: {
          label: '점검 후',
          title: '지금 기준으로 다시 정리된 보장',
          description: '불필요한 부분은 줄이고 필요한 영역은 보완하는 방향을 찾을 수 있습니다.',
        },
      },
      {
        type: 'benefit-cards',
        title: '리모델링 핵심 포인트',
        items: [
          { icon: '✂️', title: '중복 보장 점검', description: '겹치는 구조를 먼저 정리합니다.' },
          { icon: '🧩', title: '생활 변화 반영', description: '현재 상황 기준으로 다시 봅니다.' },
          { icon: '📉', title: '보험료 효율 확인', description: '지속 가능한 구조인지 점검합니다.' },
        ],
      },
    ]
  }

  return buildGenericSections(category)
}

function getTemplate2ContentSections(category) {
  if (category === 'HIDDEN_INSURANCE_MONEY') {
    return [
      {
        type: 'icon-grid',
        title: '',
        columns: 3,
        items: [
          { icon: '🔎', title: '조회 범위 확인', description: '필요한 정보와 확인 가능한 범위를 먼저 안내합니다.' },
          { icon: '📄', title: '전문 확인', description: '누락 가능성을 함께 점검합니다.' },
          { icon: '💰', title: '후속 연결', description: '필요 시 다음 절차까지 이어집니다.' },
        ],
      },
      {
        type: 'faq',
        title: '상담 전 확인 포인트',
        items: [
          { question: '입력 항목이 많은가요?', answer: '아니요. 이름과 연락처만 남겨주시면 바로 확인을 시작할 수 있습니다.' },
        ],
      },
    ]
  }

  if (category === 'COVERAGE_ANALYSIS') {
    return [
      {
        type: 'stats',
        title: '분석 전 확인 포인트',
        items: [
          { label: '중복 여부', value: '즉시 확인' },
          { label: '누락 여부', value: '우선 체크' },
          { label: '상담 방식', value: '1:1' },
        ],
      },
      {
        type: 'icon-grid',
        title: '',
        columns: 3,
        items: [
          { icon: '🛡️', title: '핵심 보장 확인', description: '필수 보장을 먼저 봅니다.' },
          { icon: '🔁', title: '중복 구조 점검', description: '겹치는 구간을 확인합니다.' },
          { icon: '💬', title: '상담 연결', description: '필요한 경우 바로 이어집니다.' },
        ],
      },
    ]
  }

  if (category === 'INSURANCE_REMODELING') {
    return [
      {
        type: 'icon-grid',
        title: '',
        columns: 3,
        items: [
          { icon: '📉', title: '보험료 부담 점검', description: '현재 납입 구조를 먼저 봅니다.' },
          { icon: '🧱', title: '보장 구조 확인', description: '중복과 누락을 정리합니다.' },
          { icon: '🛠️', title: '재구성 상담', description: '지금 기준으로 방향을 제안합니다.' },
        ],
      },
      {
        type: 'text',
        title: '부담을 줄이면서 핵심은 남기는 방식',
        content: '리모델링은 무조건 바꾸는 것이 아니라, 유지할 것과 조정할 것을 구분하는 데 목적이 있습니다.',
      },
    ]
  }

  return [
    {
      type: 'icon-grid',
      title: '',
      columns: 3,
      items: [
        { icon: '✓', title: '주요 기준 요약', description: '상담 전 확인할 기준을 정리했습니다.' },
        { icon: '✓', title: '간편 신청', description: '짧게 적고 바로 상담을 요청하실 수 있습니다.' },
        { icon: '✓', title: '맞춤 상담', description: '궁금하신 보장부터 차근차근 안내해 드립니다.' },
      ],
    },
  ]
}

function getTemplate3ContentSections(category) {
  if (category === 'HIDDEN_INSURANCE_MONEY') {
    return [
      {
        type: 'icon-grid',
        title: '',
        columns: 3,
        items: [
          { icon: '📌', title: '놓친 보험금이 있을 수 있어요', description: '청구 시한을 놓치거나 가입 내역을 \n 잊은 경우가 흔합니다.' },
          { icon: '🧾', title: '간단한 정보만으로 시작', description: '이름·연락처와 기억나는 내용만으로도 \n 신청할 수 있습니다.' },
          { icon: '📲', title: '바로 상담 요청', description: '궁금하신 순간 폼에서 바로 문의를 \n 남기실 수 있습니다.' },
        ],
      },
      {
        type: 'benefit-cards',
        title: '숨은보험금, 이렇게 진행됩니다',
        items: [
          { icon: '✅', title: '왜 지금 확인할까요', description: '미청구·미수령이 있으면 기한이 지나 \n 받기 어려울 수 있습니다.' },
          { icon: '✅', title: '복잡하지 않아요', description: '필요한 정보만 남기고 단계별로 \n 안내받으실 수 있습니다.' },
          { icon: '✅', title: '다음 단계까지 동행', description: '확인 후 필요하면 청구·추가 상담까지 이어드립니다.' },
        ],
      },
      {
        type: 'faq',
        title: '자주 묻는 질문',
        items: [
          { question: '비용이 드나요?', answer: '초기 상담과 기본 확인은 부담 없이 시작할 수 있습니다.' },
          { question: '조회 후 꼭 진행해야 하나요?', answer: '아니요. 확인 후 상담을 통해 방향을 선택하실 수 있습니다.' },
        ],
      },
    ]
  }

  if (category === 'COVERAGE_ANALYSIS') {
    return [
      {
        type: 'icon-grid',
        title: '',
        columns: 3,
        items: [
          { icon: '📌', title: '꼭 필요한 보장부터', description: '질병·상해 등 생활에서 먼저 챙겨야 할 부분부터 짚습니다.' },
          { icon: '🧾', title: '지금 가입 내역 정리', description: '여러 증권이 있어도 한눈에 구조를 정리해 드립니다.' },
          { icon: '📲', title: '분석 후 바로 상담', description: '궁금한 점을 확인한 뒤 이어서 1:1로 상담하실 수 있습니다.' },
        ],
      },
      {
        type: 'comparison',
        title: '분석 전과 후, 이렇게 달라집니다',
        before: {
          label: '분석 전',
          title: '어떤 보험에 뭘 넣었는지 막연함',
          description: '부족한지, 겹치는지, 유지해도 되는지 스스로 판단하기 어렵습니다.',
        },
        after: {
          label: '분석 후',
          title: '우선순위가 보입니다',
          description: '먼저 손볼 곳과 유지해도 되는 부분이 분명해져 다음 결정이 쉬워집니다.',
        },
      },
      {
        type: 'testimonial',
        title: '상담 후기',
        items: [
          { name: '김○○님', content: '새로 가입하라는 말보다 지금 구조를 이해하는 데 도움이 됐어요.' },
          { name: '이○○님', content: '중복되는 보장을 설명해 주셔서 훨씬 명확해졌습니다.' },
        ],
      },
    ]
  }

  if (category === 'INSURANCE_REMODELING') {
    return [
      {
        type: 'icon-grid',
        title: '',
        columns: 3,
        items: [
          { icon: '📌', title: '지금 보험이 맞는지', description: '가족·소득·생활 패턴에 맞는지 함께 봅니다.' },
          { icon: '🧾', title: '줄일 곳·채울 곳', description: '과한 보장과 부족한 보장을 구분해 드립니다.' },
          { icon: '📲', title: '맞춤 제안', description: '점검 결과를 바탕으로 현실적인 방향을 제안합니다.' },
        ],
      },
      {
        type: 'comparison',
        title: '리모델링 전후 방향',
        before: {
          label: '점검 전',
          title: '예전 기준으로 유지 중인 보험',
          description: '현재 가족 구성, 직업, 지출과 맞지 않을 수 있습니다.',
        },
        after: {
          label: '점검 후',
          title: '지금 기준으로 다시 정리된 구조',
          description: '보장은 핵심 위주로, 보험료는 효율 중심으로 검토할 수 있습니다.',
        },
      },
      {
        type: 'process-steps',
        title: '리모델링 상담 흐름',
        steps: [
          { number: 1, icon: '📝', title: '현황 확인', description: '현재 가입 상태를 먼저 봅니다.' },
          { number: 2, icon: '🔎', title: '문제 구간 진단', description: '중복과 누락, 과다 지출을 확인합니다.' },
          { number: 3, icon: '🛠️', title: '재구성 제안', description: '현실적인 방향을 제안합니다.' },
        ],
      },
    ]
  }

  return buildGenericSections(category)
}

function getGenericTheme(presetKey, category) {
  const themeMap = {
    HIDDEN_INSURANCE_MONEY: {
      default: { heroBackgroundColor: '#eff6ff', heroTextColor: '#0f172a', heroSubtitleColor: '#1d4ed8', heroScrollCtaLabel: '숨은보험금 찾아보기' },
      'clean-form': { heroBackgroundColor: '#eff6ff', heroTextColor: '#0f172a', heroSubtitleColor: '#2563eb', formBorderColor: '#2563eb', formBorderWidth: 3 },
      'hanwha-sections': { heroBackgroundColor: '#e0f2fe', heroTextColor: '#0f172a', heroSubtitleColor: '#0369a1', formBorderColor: '#0ea5e9', formBorderWidth: 3, highlightsBackgroundColor: '#f8fafc', recommendedTitle: '추천상품' },
      'basic-template-4': { heroBackgroundColor: '#dbeafe', heroTextColor: '#0f172a', heroSubtitleColor: '#1d4ed8', formBorderColor: '#2563eb', formBorderWidth: 3, ctaBandBackgroundColor: '#bfdbfe' },
      'basic-template-5': { heroBackgroundColor: '#0f172a', heroTextColor: '#ffffff', heroSubtitleColor: '#bfdbfe', formBorderColor: '#1e40af', formBorderWidth: 2, bodyBackgroundColor: '#f8fafc' },
    },
    COVERAGE_ANALYSIS: {
      default: { heroBackgroundColor: '#f8fafc', heroTextColor: '#0f172a', heroSubtitleColor: '#334155' },
      'clean-form': { heroBackgroundColor: '#eff6ff', heroTextColor: '#0f172a', heroSubtitleColor: '#1d4ed8', formBorderColor: '#2563eb', formBorderWidth: 3 },
      'hanwha-sections': { heroBackgroundColor: '#e0f2fe', heroTextColor: '#0f172a', heroSubtitleColor: '#0369a1', formBorderColor: '#0284c7', formBorderWidth: 3, highlightsBackgroundColor: '#f8fafc', recommendedTitle: '추천상품' },
      'basic-template-4': { heroBackgroundColor: '#dbeafe', heroTextColor: '#0f172a', heroSubtitleColor: '#1e3a8a', formBorderColor: '#2563eb', formBorderWidth: 3, ctaBandBackgroundColor: '#dbeafe' },
      'basic-template-5': { heroBackgroundColor: '#111827', heroTextColor: '#ffffff', heroSubtitleColor: '#cbd5e1', formBorderColor: '#1d4ed8', formBorderWidth: 2, bodyBackgroundColor: '#f8fafc' },
    },
    INSURANCE_REMODELING: {
      default: { heroBackgroundColor: '#f7fee7', heroTextColor: '#14532d', heroSubtitleColor: '#15803d' },
      'clean-form': { heroBackgroundColor: '#f0fdf4', heroTextColor: '#14532d', heroSubtitleColor: '#16a34a', formBorderColor: '#16a34a', formBorderWidth: 3 },
      'hanwha-sections': { heroBackgroundColor: '#dcfce7', heroTextColor: '#14532d', heroSubtitleColor: '#15803d', formBorderColor: '#65a30d', formBorderWidth: 3, highlightsBackgroundColor: '#f7fee7', recommendedTitle: '추천상품' },
      'basic-template-4': { heroBackgroundColor: '#dcfce7', heroTextColor: '#14532d', heroSubtitleColor: '#15803d', formBorderColor: '#16a34a', formBorderWidth: 3, ctaBandBackgroundColor: '#bbf7d0' },
      'basic-template-5': { heroBackgroundColor: '#14532d', heroTextColor: '#ffffff', heroSubtitleColor: '#dcfce7', formBorderColor: '#16a34a', formBorderWidth: 2, bodyBackgroundColor: '#f7fee7' },
    },
  }
  const presetTheme = themeMap[category]?.[presetKey]
  if (presetTheme) return presetTheme
  if (presetKey === 'lead-gen-fullbleed') {
    const base = themeMap[category]?.['hanwha-sections']
    if (base) {
      return {
        ...base,
        heroBandBackgroundColor: '#ECEAFF',
        detailStripBackgroundColor: '#F7F7F7',
        formBorderColor: '#f47321',
        formBorderWidth: 2,
      }
    }
    return {
      heroBandBackgroundColor: '#ECEAFF',
      detailStripBackgroundColor: '#F7F7F7',
      formBorderColor: '#f47321',
      formBorderWidth: 2,
      highlightsBackgroundColor: '#f8fafc',
      recommendedTitle: '추천상품',
    }
  }
  return null
}

function getTemplate4ContentSections(category) {
  if (category === 'HIDDEN_INSURANCE_MONEY') {
    return [
      {
        type: 'stats',
      title: '조회 전 확인하세요',
        items: [
          { label: '확인 범위', value: '상담 기준' },
          { label: '확인 단계', value: '3단계' },
          { label: '전문가 검토', value: '1:1' },
          { label: '후속 안내', value: '지속 제공' },
        ],
      },
      {
        type: 'process-steps',
        title: '숨은 보험금 확인 절차',
        steps: [
          { number: 1, icon: '📝', title: '정보 입력', description: '기본 정보만 간단히 남겨주세요.' },
          { number: 2, icon: '🔎', title: '가능성 확인', description: '누락된 보험금 가능성을 함께 점검합니다.' },
          { number: 3, icon: '📞', title: '후속 상담', description: '필요 시 청구 방향까지 연결합니다.' },
        ],
      },
      {
        type: 'benefit-cards',
        title: '지금 확인하면 좋은 이유',
        items: [
          { icon: '💡', title: '왜 조회해야 하는지', description: '미수령 가능성과 기한을 놓치지 않도록 안내합니다.' },
          { icon: '📋', title: '진행 순서가 분명해요', description: '신청부터 확인·상담까지 단계를 미리 알 수 있습니다.' },
          { icon: '📲', title: '순차 안내', description: '남겨 주신 연락처로 상담 순서에 따라 안내해 드립니다.' },
        ],
      },
    ]
  }

  if (category === 'COVERAGE_ANALYSIS') {
    return [
      {
        type: 'stats',
        title: '보장분석, 이렇게 진행됩니다',
        items: [
          { label: '중복 보장 발견', value: '자주 발생' },
          { label: '누락 보장 확인', value: '핵심' },
          { label: '분석 방식', value: '1:1' },
          { label: '상담 방식', value: '1:1' },
        ],
      },
      {
        type: 'comparison',
        title: '분석 전후 이해 차이',
        before: {
          label: '분석 전',
          title: '보험 구조가 복잡하고 막연함',
          description: '무엇이 부족한지, 중복인지, 계속 유지해야 하는지 판단하기 어렵습니다.',
        },
        after: {
          label: '분석 후',
          title: '보완할 부분이 분명해집니다',
          description: '어디를 먼저 손볼지, 무엇을 유지할지 기준이 생깁니다.',
        },
      },
      {
        type: 'process-steps',
        title: '보장분석 진행 흐름',
        steps: [
          { number: 1, icon: '🧾', title: '현재 가입 내용 확인', description: '가입 상태를 먼저 파악합니다.' },
          { number: 2, icon: '📊', title: '중복/누락 분석', description: '핵심 보장 중심으로 구조를 점검합니다.' },
          { number: 3, icon: '💬', title: '맞춤 방향 제안', description: '현재 상황에 맞는 보완 방향을 안내합니다.' },
        ],
      },
    ]
  }

  if (category === 'INSURANCE_REMODELING') {
    return [
      {
        type: 'comparison',
        title: '리모델링 전후 방향',
        before: {
          label: '점검 전',
          title: '보험료 부담과 구조의 비효율',
          description: '중복 보장, 오래된 담보, 현재 생활과 맞지 않는 설계가 남아 있을 수 있습니다.',
        },
        after: {
          label: '점검 후',
          title: '현재 상황에 맞는 구조 재정리',
          description: '불필요한 부분은 줄이고 필요한 영역은 보완하는 방향을 잡을 수 있습니다.',
        },
      },
      {
        type: 'benefit-cards',
        title: '리모델링 상담 포인트',
        items: [
          { icon: '✂️', title: '중복 점검', description: '과하게 들어간 보장을 분리합니다.' },
          { icon: '🧩', title: '현재 생활 반영', description: '가족 구성과 직업, 소득 변화를 반영합니다.' },
          { icon: '📉', title: '보험료 효율 확인', description: '지출 대비 구조를 다시 정리합니다.' },
        ],
      },
      {
        type: 'process-steps',
        title: '리모델링 진행 절차',
        steps: [
          { number: 1, icon: '📝', title: '현재 보험 확인', description: '가입 중인 보험을 먼저 살펴봅니다.' },
          { number: 2, icon: '🔎', title: '문제 구간 점검', description: '중복, 누락, 과다 보험료 여부를 확인합니다.' },
          { number: 3, icon: '🛠️', title: '재구성 제안', description: '현재 상황에 맞는 방향을 설명드립니다.' },
        ],
      },
    ]
  }

  return [
    {
      type: 'stats',
      title: '상담 전 확인하는 주요 포인트',
      items: [
        { label: '확인 항목', value: '주요 기준' },
        { label: '상담 방식', value: '1:1' },
        { label: '맞춤 안내', value: '1:1' },
        { label: '후속 관리', value: '지속 제공' },
      ],
    },
    ...buildGenericSections(category),
  ]
}

function getTemplate5ContentSections(category) {
  if (category === 'HIDDEN_INSURANCE_MONEY') {
    return [
      {
        type: 'text',
        title: '놓치기 쉬운 보험금, 왜 생길까요?',
        content: '청구 기한을 놓치거나, 예전에 가입한 보험을 잊고 지나가는 경우가 생각보다 많습니다. 지금 한 번 확인해 두시면 내 권리를 되찾을 기회가 될 수 있습니다.',
      },
      {
        type: 'icon-grid',
        title: '',
        columns: 3,
        items: [
          { icon: '🔍', title: '누락 가능성 확인', description: '내가 놓친 보험금이 있는지 먼저 점검합니다.' },
          { icon: '🧾', title: '기억나는 정보 정리', description: '알고 있는 범위만 입력해도 시작할 수 있습니다.' },
          { icon: '☎️', title: '후속 상담 연결', description: '필요 시 다음 단계까지 자연스럽게 이어집니다.' },
        ],
      },
      {
        type: 'faq',
        title: '많이 묻는 질문',
        items: [
          { question: '기억나는 보험사가 없어도 되나요?', answer: '네. 알고 있는 범위만 남겨주셔도 상담을 시작할 수 있습니다.' },
          { question: '조회 후 바로 청구가 진행되나요?', answer: '상황 확인 후 필요한 경우 청구 방향과 절차를 별도로 안내드립니다.' },
        ],
      },
    ]
  }

  if (category === 'COVERAGE_ANALYSIS') {
    return [
      {
        type: 'text',
        title: '복잡한 보험, 이렇게 정리해 드립니다',
        content: '무조건 새로 가입하라는 상담이 아니라, 지금 가입하신 보험의 구조를 이해하고 부족하거나 겹치는 부분을 먼저 짚어 드립니다.',
      },
      {
        type: 'icon-grid',
        title: '',
        columns: 3,
        items: [
          { icon: '🛡️', title: '부족한 보장 체크', description: '가장 먼저 보완이 필요한 부분을 찾습니다.' },
          { icon: '🔁', title: '중복 보장 점검', description: '겹치는 구조가 있는지 확인합니다.' },
          { icon: '💰', title: '보험료 효율 검토', description: '지출 대비 체감 효율을 살펴봅니다.' },
        ],
      },
      {
        type: 'testimonial',
        title: '상담 후기',
        items: [
          { name: '김○○님', content: '무엇이 부족한지 몰랐는데 정리 포인트가 명확해졌어요.' },
          { name: '이○○님', content: '지금 구조를 이해하니 앞으로 무엇을 보면 될지 알겠더라고요.' },
        ],
      },
    ]
  }

  if (category === 'INSURANCE_REMODELING') {
    return [
      {
        type: 'text',
        title: '지금 보험, 계속 유지해도 괜찮을까요?',
        content: '가족 구성, 직업, 소득, 건강 상태가 달라졌다면 기존 보험 구조가 지금도 적절한지 다시 확인해볼 필요가 있습니다.',
      },
      {
        type: 'icon-grid',
        title: '',
        columns: 3,
        items: [
          { icon: '📉', title: '보험료 부담 점검', description: '지속 가능한 수준인지 먼저 살펴봅니다.' },
          { icon: '🧱', title: '보장 구조 재정리', description: '필요 없는 부분과 부족한 부분을 구분합니다.' },
          { icon: '🧭', title: '현재 상황 중심 설계', description: '과거가 아니라 지금 기준으로 방향을 잡습니다.' },
        ],
      },
      {
        type: 'comparison',
        title: '리모델링 상담의 기대 효과',
        before: {
          label: '상담 전',
          title: '무엇을 유지하고 바꿔야 할지 모호함',
          description: '보험료는 나가지만 구조가 적절한지 확신이 들지 않습니다.',
        },
        after: {
          label: '상담 후',
          title: '유지/조정 우선순위가 선명해짐',
          description: '현실적인 조정 포인트를 기준으로 다음 선택을 할 수 있습니다.',
        },
      },
    ]
  }

  return [
    {
      type: 'text',
      title: '한눈에 보는 핵심 안내',
      content: `${getCategoryLabel(category)}의 핵심 내용을 먼저 확인하고 지금 필요한 상담으로 바로 이어가실 수 있습니다.`,
    },
    ...buildGenericSections(category),
  ]
}

export function getGenericCodeTemplatePreset(category, presetKey = 'default') {
  const copy = getGenericCopy(category, presetKey)
  const includeMessage = presetKey !== 'clean-form'
  const formFields = createCategoryLandingDefaultFormFields({ includeMessage })
  const surveyCustomFields = buildDefaultSurveyCustomFields(category)

  if (presetKey === 'clean-form') {
    return {
      key: `${category.toLowerCase()}-clean-form`,
      category,
      name: `${getCategoryLabel(category)} 기본 템플릿 2 - 간편 문의형`,
      heroTitle: copy.heroTitle,
      heroSubtitle: copy.heroSubtitle,
      heroImageFileId: getCategoryLandingHeroImage(category),
      formTitle: copy.formTitle || '간편 상담 신청',
      contentSections: getTemplate2ContentSections(category),
      formFields,
      surveyCustomFields,
      customConfig: {
        templateStyle: 'clean-form',
        heroBackgroundColor: '#f8f9fa',
        heroTextColor: '#1e293b',
        heroSubtitleColor: '#2563eb',
        formBorderColor: '#3b82f6',
        formBorderWidth: 3,
        ...(getGenericTheme('clean-form', category) || {}),
      },
    }
  }

  if (presetKey === 'hanwha-sections') {
    return {
      key: `${category.toLowerCase()}-hanwha-sections`,
      category,
      name: `${getCategoryLabel(category)} 기본 템플릿 3 - 설명형`,
      heroTitle: copy.heroTitle,
      heroSubtitle: copy.heroSubtitle,
      heroImageFileId: getCategoryLandingHeroImage(category),
      formTitle: copy.formTitle || '간편 상담 신청',
      contentSections: getTemplate3ContentSections(category),
      formFields: createCategoryLandingDefaultFormFields({ includeMessage: true }),
      surveyCustomFields,
      customConfig: {
        templateStyle: 'hanwha-sections',
        heroBackgroundColor: '#e8f7f3',
        heroTextColor: '#111827',
        heroSubtitleColor: '#0f766e',
        formBorderColor: '#ff7b1b',
        formBorderWidth: 3,
        highlightsBackgroundColor: '#f8fafc',
        recommendedTitle: '추천상품',
        ...(getGenericTheme('hanwha-sections', category) || {}),
      },
    }
  }

  if (presetKey === 'lead-gen-fullbleed') {
    return {
      key: `${category.toLowerCase()}-lead-gen-fullbleed`,
      category,
      name: `${getCategoryLabel(category)} 기본 템플릿 6 - 풀블리드 전환형`,
      heroTitle: copy.heroTitle,
      heroSubtitle: copy.heroSubtitle,
      heroImageFileId: getCategoryLandingHeroImage(category),
      formTitle: copy.formTitle || '간편 상담 신청',
      contentSections: getTemplate3ContentSections(category),
      formFields: createCategoryLandingDefaultFormFields({ includeMessage: true }),
      surveyCustomFields,
      customConfig: {
        templateStyle: leadGenFullbleedTemplateStyleForCategory(category),
        heroBandBackgroundColor: '#ECEAFF',
        detailStripBackgroundColor: '#F7F7F7',
        heroDetailImageFileId: '',
        formBorderColor: '#f47321',
        formBorderWidth: 2,
        highlightsBackgroundColor: '#f8fafc',
        recommendedTitle: '추천상품',
        ...(getGenericTheme('lead-gen-fullbleed', category) || {}),
      },
    }
  }

  if (presetKey === 'basic-template-4') {
    return {
      key: `${category.toLowerCase()}-basic-template-4`,
      category,
      name: `${getCategoryLabel(category)} 기본 템플릿 4 - CTA 강조형`,
      heroTitle: copy.heroTitle,
      heroSubtitle: copy.heroSubtitle,
      heroImageFileId: getCategoryLandingHeroImage(category),
      formTitle: copy.formTitle || '간편 상담 신청',
      contentSections: getTemplate4ContentSections(category),
      formFields: createCategoryLandingDefaultFormFields({ includeMessage: true }),
      surveyCustomFields,
      customConfig: {
        templateStyle: 'basic-template-4',
        heroBackgroundColor: '#eef4ff',
        heroTextColor: '#0f172a',
        heroSubtitleColor: '#334155',
        formBorderColor: '#2563eb',
        formBorderWidth: 3,
        ctaBandBackgroundColor: '#dbeafe',
        ...(getGenericTheme('basic-template-4', category) || {}),
      },
    }
  }

  if (presetKey === 'basic-template-5') {
    return {
      key: `${category.toLowerCase()}-basic-template-5`,
      category,
      name: `${getCategoryLabel(category)} 기본 템플릿 5 - 프리미엄 상담형`,
      heroTitle: copy.heroTitle,
      heroSubtitle: copy.heroSubtitle,
      heroImageFileId: getCategoryLandingHeroImage(category),
      formTitle: copy.formTitle || '상담 신청',
      contentSections: getTemplate5ContentSections(category),
      formFields: createCategoryLandingDefaultFormFields({ includeMessage: true }),
      surveyCustomFields,
      customConfig: {
        templateStyle: 'basic-template-5',
        heroBackgroundColor: '#111827',
        heroTextColor: '#ffffff',
        heroSubtitleColor: '#cbd5e1',
        formBorderColor: '#0f172a',
        formBorderWidth: 2,
        bodyBackgroundColor: '#f8fafc',
        ...(getGenericTheme('basic-template-5', category) || {}),
      },
    }
  }

  return {
    key: `${category.toLowerCase()}-default`,
    category,
    name: `${getCategoryLabel(category)} 기본 템플릿 1 - 일반형`,
    heroTitle: copy.heroTitle,
    heroSubtitle: copy.heroSubtitle,
    heroImageFileId: getCategoryLandingHeroImage(category),
    formTitle: copy.formTitle || '상담 신청',
    contentSections: getTemplate1ContentSections(category),
    formFields,
    surveyCustomFields,
    customConfig: {
      templateStyle: 'default',
      heroBackgroundColor: '#f8fafc',
      heroTextColor: '#0f172a',
      heroSubtitleColor: '#475569',
      ...(getGenericTheme('default', category) || {}),
    },
  }
}
